import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import AdminPage from '../../components/admin/AdminPage';
import MetricCard from '../../components/admin/MetricCard';
import notificationsService, {
  GlobalAnalytics,
} from '../../services/NotificationsService';

const daysAgoIso = (n: number): string =>
  new Date(Date.now() - n * 86400 * 1000).toISOString().slice(0, 10);

const NotificationAnalyticsPage: React.FC = () => {
  const [from, setFrom] = useState<string>(daysAgoIso(30));
  const [to, setTo] = useState<string>(daysAgoIso(0));
  const [analytics, setAnalytics] = useState<GlobalAnalytics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await notificationsService.globalAnalytics({
        from: new Date(`${from}T00:00:00`).toISOString(),
        to: new Date(`${to}T23:59:59`).toISOString(),
      });
      setAnalytics(result);
    } catch {
      setError('Failed to load analytics.');
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <AdminPage
      title="Notification analytics"
      description="Aggregate delivery and engagement across all notifications in a time window."
    >
      <Stack direction="row" spacing={2} alignItems="center">
        <TextField
          size="small"
          type="date"
          label="From"
          InputLabelProps={{ shrink: true }}
          value={from}
          onChange={(e) => setFrom(e.target.value)}
        />
        <TextField
          size="small"
          type="date"
          label="To"
          InputLabelProps={{ shrink: true }}
          value={to}
          onChange={(e) => setTo(e.target.value)}
        />
      </Stack>

      {error ? <Alert severity="error">{error}</Alert> : null}

      {loading || !analytics ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={3}>
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}>
              <MetricCard label="Sent" value={analytics.totals.sent.toLocaleString()} />
            </Grid>
            <Grid item xs={6} md={3}>
              <MetricCard
                label="Failed"
                value={analytics.totals.failed.toLocaleString()}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <MetricCard
                label="Suppressed"
                value={analytics.totals.suppressed.toLocaleString()}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <MetricCard
                label="Open rate"
                value={formatRate(analytics.openRate)}
                helperText={`${analytics.events.opened.toLocaleString()} opens`}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <MetricCard
                label="CTR"
                value={formatRate(analytics.clickThroughRate)}
                helperText={`${analytics.events.clicked.toLocaleString()} clicks`}
              />
            </Grid>
          </Grid>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Sent by category
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Category</TableCell>
                      <TableCell align="right">Sent</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analytics.byCategory.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={2} align="center">
                          <Typography color="text.secondary" sx={{ py: 2 }}>
                            No sends in this window.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      analytics.byCategory.map((row) => (
                        <TableRow key={row.category}>
                          <TableCell>{row.category}</TableCell>
                          <TableCell align="right">
                            {row.sent.toLocaleString()}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Stack>
      )}
    </AdminPage>
  );
};

const formatRate = (rate: number | null): string =>
  rate == null ? '—' : `${(rate * 100).toFixed(1)}%`;

export default NotificationAnalyticsPage;
